import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, Box, Inscription, RoundCloseIcon, WarningIcon } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import Permissions from '@/common/permissions';
import { ModalWrapper } from '@/components';
import { useHasAccess, useHistory, useSnackbar } from '@/modules/core';
import { SnackbarState } from '@/modules/core/core.types';

import { PersonalInformationPayload } from '../PersonalInformation/stores/usePersonalInformation.store';
import LinkedAccounts from '../RegistrationAndLogin/LinkedAccounts';
import useLinkedEmployeesQuery from '../RegistrationAndLogin/LinkedAccounts/useLinkedEmployees.query';

import AccountsChecking from './AccountsChecking';
import PoliciesChecking from './PoliciesChecking';
import useMarkEmployeeAsBadDataQuery from './useMarkEmployeeAsBadData.query';

interface Props {
  employee: Partial<PersonalInformationPayload> | null;
  isEditMode: boolean;
}

const MarkBadData: React.FC<Props> = ({ employee, isEditMode }) => {
  const hasAccessToLinkEmployeeAccount = useHasAccess([{ permission: Permissions.LINK_EMPLOYEE_ACCOUNT }]);
  const [isAccountsCheckedSuccessfully, setIsAccountsCheckedSuccessfully] = useState(false);
  const [isPoliciesCheckedSuccessfully, setIsPoliciesCheckedSuccessfully] = useState(false);
  const isSuccess = useMemo(
    () => isAccountsCheckedSuccessfully && isPoliciesCheckedSuccessfully,
    [isAccountsCheckedSuccessfully, isPoliciesCheckedSuccessfully]
  );
  const [hasMarkDataModal, setHasMarkDataModal] = useState(false);
  const [hasLinkedAccountsModal, setHasLinkedAccountsModal] = useState(false);
  const markBadDataTitle = useMemo(() => `Mark ${employee?.fullName} as Bad Data?`, [employee]);
  const {
    formattedData: { linkedEmployees },
    isLoading,
  } = useLinkedEmployeesQuery(hasAccessToLinkEmployeeAccount ? employee?.employeeId : undefined);
  const hasLinkedAccounts = useMemo(() => Boolean(linkedEmployees?.length), [linkedEmployees]);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const history = useHistory();
  const onSuccess = useCallback(() => {
    history.push(ROUTES.EMPLOYEES);
    handleAddPermanentSnackbar({
      text: `${employee?.fullName} was marked as Bad Data`,
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [employee, handleAddPermanentSnackbar, history]);
  const { markEmployeeAsBadData } = useMarkEmployeeAsBadDataQuery(employee?.employeeId, onSuccess);
  const handleSubmit = useCallback(
    (newPrimaryEmployeeId?: string) => {
      setHasMarkDataModal(false);
      if (hasLinkedAccounts && !newPrimaryEmployeeId) {
        setHasLinkedAccountsModal(true);
        return;
      }

      markEmployeeAsBadData(newPrimaryEmployeeId);
    },
    [setHasMarkDataModal, hasLinkedAccounts, setHasLinkedAccountsModal, markEmployeeAsBadData]
  );

  return (
    <>
      <Box data-testId="MarkBadData" background="module" round="moduleRound" pad="spacing24">
        <Box
          pad="spacing24"
          round="container1Round"
          background="canvas"
          border={{ color: 'border2', size: 'small' }}
          width="fit-content"
          align="center"
        >
          <Inscription color="textTitle" weight="bold" size="24px" lineHeight="32px">
            Mark as Bad Data
          </Inscription>

          <Box margin={{ top: 'spacing24' }}>
            <AppButton
              onClick={() => setHasMarkDataModal(true)}
              color="red"
              size="L"
              width="416px"
              disabled={!isEditMode}
              testId={`markAsBadData_${isEditMode ? 'notDisabled' : 'disabled'}`}
            >
              Mark this Employee as Bad Data
            </AppButton>
          </Box>
        </Box>
      </Box>

      {hasLinkedAccountsModal ? (
        <LinkedAccounts
          isEditMode
          changeOnlyPrimary={{
            description:
              'The account being deleted is currently linked to other accounts. Select one of the account below to become the new primary account.',
            onClose: () => setHasLinkedAccountsModal(false),
            onSave: (primaryEmployeeId: string) => handleSubmit(primaryEmployeeId),
          }}
        />
      ) : null}

      <ModalWrapper
        testId="MarkBadData"
        visible={hasMarkDataModal}
        onSetVisible={setHasMarkDataModal}
        title={markBadDataTitle}
        description={
          <Inscription color="textBody" margin={{ top: 'spacing8' }} lineHeight="20px">
            The following conditions must be met before marking a record as bad data:
          </Inscription>
        }
      >
        <Box margin={{ bottom: 'spacing16' }} background="module" round="moduleRound" pad="spacing24">
          <Box
            background="canvas"
            round="container1Round"
            border={{
              size: 'small',
              color: 'border2',
            }}
            pad="spacing24"
            direction="row"
          >
            <Box width="50%">
              <AccountsChecking
                employee={employee}
                onSuccess={() => setIsAccountsCheckedSuccessfully(true)}
              />
            </Box>
            <Box width="50%">
              <PoliciesChecking
                employee={employee}
                onSuccess={() => setIsPoliciesCheckedSuccessfully(true)}
              />
            </Box>
          </Box>
        </Box>

        {isSuccess ? null : (
          <Box
            data-testid="MarkBadData_error_label"
            pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
            background={{ color: 'dangerContainer' }}
            border={{ size: '1px', color: 'dangerBorder' }}
            round="fieldRound"
            justify="center"
            direction="row"
            margin={{ bottom: 'spacing16' }}
          >
            <Box width={{ min: '24px' }} align="center">
              <RoundCloseIcon size="20px" color="danger" />
            </Box>
            <Inscription weight="bold" color="textBody" lineHeight="20px" margin={{ left: 'spacing8' }}>
              One or more requirements haven’t been met. Once both conditions are satisfied, you’ll be able to
              continue.
            </Inscription>
          </Box>
        )}

        <Box
          data-testid="MarkBadData_warning_label"
          pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
          background={{ color: 'warningContainer' }}
          border={{ size: '1px', color: 'warningBorder' }}
          round="fieldRound"
          justify="center"
          direction="row"
        >
          <WarningIcon />
          <Inscription weight="bold" color="textBody" lineHeight="20px" margin={{ left: 'spacing8' }}>
            Once you will mark employee as Bad Data it will be permanently deleted. This action is
            irreversible.
          </Inscription>
        </Box>

        <Box direction="row" pad={{ top: 'spacing24' }} justify="end">
          <AppButton
            testId="MarkBadData_Modal_Cancel"
            onClick={() => setHasMarkDataModal(false)}
            buttonType="secondary"
            style={{ minWidth: '160px' }}
          >
            Cancel
          </AppButton>
          <Box margin={{ left: 'spacing12' }}>
            <AppButton
              testId="MarkBadData_Modal_Submit"
              onClick={() => handleSubmit()}
              color="red"
              style={{ minWidth: '160px' }}
              disabled={!isSuccess || isLoading}
            >
              Submit
            </AppButton>
          </Box>
        </Box>
      </ModalWrapper>
    </>
  );
};

export default MarkBadData;
